import React, { useEffect, useState, useRef, useMemo } from "react";
import classes from "./PurchaseOrder.module.css";
import HeaderNav from "../../../Components/Dahboard Header/HeaderNav/HeaderNav";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import searchicon from "../../../Assets/searchicon.png";
import filter from "../../../Assets/filter.png";
import Footer from "../../../Components/Footer/Footer";
import { Pen, Trash2, Printer, Eye } from "lucide-react";
import Swal from "sweetalert2";
import api, { withCompanyId } from "../../../Components/API/Api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";


const StockRequisition = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewMessage, setViewMessage] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewActionLoading, setViewActionLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState(null);
  const [editDepartments, setEditDepartments] = useState([]);
  const [editStockOptions, setEditStockOptions] = useState([]);
  const [editForm, setEditForm] = useState({
    request_id: "",
    narration: "",
    department_id: "",
    items: [{ stock_id: "", stock_name: "", quantity: 1 }],
  });
  const printRef = useRef();
  const pageMap = {
    "/dashboard": "Dashboard",
    "/transactions": "Transactions",
    "/accounts": "Accounting",
    "/reports": "Reports",
    "/banking": "Banking",
    "/settings": "Settings",
  };





  const extractRequisitionList = (response) => {
    const body = response?.data ?? {};
    const payload = body?.data ?? body;

    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === "object") {
      if (Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload.transactions?.data)) return payload.transactions.data;
      if (Array.isArray(payload.transactions)) return payload.transactions;
      if (Array.isArray(payload.requisitions)) return payload.requisitions;
    }
    if (Array.isArray(body)) return body;

    return [];
  };

  const pickNarration = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    const value = obj.narration;
    if (value == null || String(value).trim() === "") return null;
    return String(value).trim();
  };

  const extractDetailEntries = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload !== "object") return [];

    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.transactions?.data)) return payload.transactions.data;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.stock_items)) return payload.stock_items;
    if (Array.isArray(payload.requisition_items)) return payload.requisition_items;

    return [payload];
  };

  const extractNarrationFromDetail = (payload) => {
    if (!payload) return null;

    const direct =
      pickNarration(payload) ??
      pickNarration(payload.requisition) ??
      pickNarration(payload.stock_requisition) ??
      pickNarration(payload.transaction);
    if (direct) return direct;

    const entries = extractDetailEntries(payload);
    for (const entry of entries) {
      const value =
        pickNarration(entry) ??
        pickNarration(entry?.requisition) ??
        pickNarration(entry?.stock_requisition) ??
        pickNarration(entry?.transaction);
      if (value) return value;
    }

    return null;
  };

  const fetchRequisitionDetails = async (requestId) => {
    const res = await api.get("/v1/stocks/fetch-requisitions-by-id", {
      params: withCompanyId({ request_id: requestId }),
    });

    return res.data?.data ?? res.data ?? null;
  };

  const normalizeRequisitionItem = (raw) => {
    if (!raw || typeof raw !== "object") return raw;

    const nested = raw.requisition ?? raw.transaction ?? raw.stock_requisition ?? null;
    const base = nested && typeof nested === "object" ? nested : raw;

    return {
      ...raw,
      ...base,
      narration:
        base.narration ??
        raw.narration ??
        null,
      created_at:
        base.created_at ??
        raw.created_at ??
        base.date ??
        raw.date ??
        base.updated_at ??
        raw.updated_at ??
        base.processed_date ??
        raw.processed_date ??
        null,
      request_id: base.request_id ?? raw.request_id ?? base.order_id ?? raw.order_id,
      order_id: base.order_id ?? raw.order_id,
      department: base.department ?? raw.department,
      quantity: base.total ?? base.quantity ?? raw.total ?? raw.quantity,
      approval_status:
        base.approval_status ??
        raw.approval_status ??
        base.status ??
        raw.status,
    };
  };

  const applyNarrationMap = (list, narrationMap) =>
    list.map((item) => {
      const requestId = item.request_id || item.order_id;
      return {
        ...item,
        narration: item.narration ?? (requestId ? narrationMap[requestId] : null) ?? null,
      };
    });

  const fetchInvoices = async () => {
    try {
      const res = await api.get("/v1/stocks/fetch-requisitions", {
        params: withCompanyId({}),
      });
      const list = extractRequisitionList(res).map(normalizeRequisitionItem);
      setInvoices(list);
    } catch (err) {
      console.error("Error fetching requisitions:", err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const groupedRequisitions = useMemo(() => {
    const map = new Map();

    invoices.forEach((item) => {
      const key = item.request_id || item.order_id || String(item.id);
      const lineQty = Number(item.total ?? item.quantity ?? 0);

      if (!map.has(key)) {
        map.set(key, {
          ...item,
          narration: item.narration ?? null,
          created_at: item.created_at || item.date || item.updated_at || item.processed_date || null,
          totalQuantity: Number.isFinite(lineQty) ? lineQty : 0,
        });
        return;
      }

      const existing = map.get(key);
      existing.totalQuantity += Number.isFinite(lineQty) ? lineQty : 0;
      if (!existing.narration && item.narration) {
        existing.narration = item.narration;
      }
      if (!existing.created_at && (item.created_at || item.date || item.updated_at || item.processed_date)) {
        existing.created_at = item.created_at || item.date || item.updated_at || item.processed_date;
      }
    });

    return Array.from(map.values());
  }, [invoices]);

  const cardMetrics = useMemo(() => {
    let totalAmount = 0;
    let pendingAmount = 0;
    let releasedAmount = 0;
    let approvedCount = 0;
    let totalQuantity = 0;

    groupedRequisitions.forEach((item) => {
      const amt = Number(item.amount ?? item.total ?? item.total_amount ?? 0) || 0;
      totalAmount += amt;

      const status = String(item.approval_status ?? item.status ?? "").trim();
      if (status === "0") pendingAmount += amt;
      if (status === "1") {
        releasedAmount += amt;
        approvedCount += 1;
      }

      totalQuantity += Number(item.totalQuantity ?? 0) || 0;
    });

    return { totalAmount, pendingAmount, releasedAmount, approvedCount, totalQuantity };
  }, [groupedRequisitions]);

  const formatMoney = (val) => `${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const statusClass = (status) => {
    if (status === "Success") return classes.greenStatus;
    if (status === "Pending") return classes.yellowStatus;
    return classes.redStatus;
  };


  const handlePrint = (rowData) => {
    navigate("/invoice-previeww", {
      state: { invoice: rowData },
    });
  };

  const getApprovalStatusClass = (item) => {
    const status = item.approval_status ?? item.status;
    const released = item.is_released ?? item.released;
    if (released === "1" || released === 1) return classes.greenStatus;
    if (status === "1" || status === 1) return classes.greenStatus;
    if (status === "0" || status === 0) return classes.yellowStatus;
    if (status === "2" || status === 2) return classes.redStatus;
    return classes.yellowStatus;
  };

  const getDeliveryStatus = (item) => {
    if (item.is_supplied == 1) return { text: "Delivered", class: classes.greenStatus };
    if (item.is_supplied == 0) return { text: "Pending", class: classes.yellowStatus };

    if (item.status == "0") return { text: "Pending", class: classes.yellowStatus };
    if (item.status == "1") return { text: "Delivered", class: classes.greenStatus };
    if (item.status == "2") return { text: "Overdue", class: classes.redStatus };

    // ✅ Always return a fallback
    return { text: "Pending", class: classes.yellowStatus };
  };

  const getPaymentStatus = (item) => {
    const paid = Number(item.paid || 0);
    const amount = Number(item.amount || 0);
    const balance = Number(item.balance || 0);

    if (balance > 0) return { text: "Pending", class: classes.yellowStatus };
    if (balance == 0) return { text: "Paid", class: classes.greenStatus };
    if (paid == amount) return { text: "Paid", class: classes.greenStatus };

    // ✅ Always return a fallback
    return { text: "Pending", class: classes.yellowStatus };
  };

  const formatPostDate = (value) => {
    if (value == null || value === "") return "—";
    const str = String(value).trim();
    if (!str) return "—";
    if (str.includes("T")) return str.split("T")[0];
    if (str.includes(" ")) return str.split(" ")[0];
    return str.length >= 10 ? str.slice(0, 10) : str;
  };

  const getNarration = (item) => {
    const value = item?.narration;
    if (value == null || String(value).trim() === "") return "—";
    return String(value).trim();
  };

  const truncateNarration = (text, maxLength = 30) => {
    if (!text || text === "—") return "—";
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };

  const getPostDate = (item) =>
    formatPostDate(item?.created_at ?? item?.date ?? item?.updated_at ?? item?.processed_date ?? item?.approved_date);

  const getApprovalStatusLabel = (item) => {
    const status = item.approval_status ?? item.status;
    const released = item.is_released ?? item.released;
    if (released === "1" || released === 1) return "Released";
    if (status === "0" || status === 0) return "Pending";
    if (status === "1" || status === 1) return "Approved";
    if (status === "2" || status === 2) return "Rejected";
    return status ?? "Pending";
  };

  const isRequisitionApproved = (item) => {
    const status = item.approval_status ?? item.status;
    const released = item.is_released ?? item.released;
    return released === "1" || released === 1 || status === "1" || status === 1;
  };

  const fetchViewData = fetchRequisitionDetails;

  const getViewActionInfo = (data) => {
    const item = Array.isArray(data) ? data[0] : data;
    const approvalStatus = String(item?.approval_status ?? item?.status ?? "").trim();
    const isReleased = String(item?.is_released ?? "").trim();

    if (approvalStatus === "0") {
      return { label: "Approve", endpoint: "/v1/stocks/approve-requisition" };
    }
    if (approvalStatus === "1" && (isReleased === "0" || isReleased === "")) {
      return { label: "Release", endpoint: "/v1/stocks/release-requisition" };
    }
    return null;
  };

  const handleView = async (item) => {
    const requestId = item?.request_id || item?.order_id || item?.id;
    setShowViewModal(true);
    setViewLoading(true);
    try {
      const data = await fetchViewData(requestId);
      setViewData(data);
    } catch (err) {
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleViewAction = async () => {
    const action = getViewActionInfo(viewData);
    const item = Array.isArray(viewData) ? viewData[0] : viewData;
    const requestId = item?.request_id || item?.order_id || item?.id;
    const companyId = localStorage.getItem("activeCompanyId") || localStorage.getItem("company_id");

    if (!action || !requestId || !companyId) return;

    setViewActionLoading(true);
    try {
      const response = await api.post(action.endpoint, {
        company_id: companyId,
        request_id: requestId,
      });

      const responseBody = response?.data || {};
      if (responseBody?.status) {
        setViewMessage(responseBody.message || "Action completed successfully.");
      } else {
        setViewMessage(responseBody.message || "Action failed. Please try again.");
      }

      const refreshedData = await fetchViewData(requestId);
      setViewData(refreshedData);
      await fetchInvoices();
    } catch (err) {
      console.error("View action failed", err);
      setViewMessage("Action failed. Please try again.");
    } finally {
      setViewActionLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const requestId = item?.request_id || item?.order_id || item?.id;
    const companyId = localStorage.getItem("activeCompanyId") || localStorage.getItem("company_id");
    if (!requestId || !companyId) return;

    const result = await Swal.fire({
      title: "Delete requisition?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.post("/v1/stocks/delete-requisition", {
        company_id: companyId,
        request_id: requestId,
      });

      await fetchInvoices();

      await Swal.fire({
        title: "Deleted",
        text: response?.data?.message || "Requisition deleted successfully.",
        icon: "success",
      });
    } catch (err) {
      console.error("Failed to delete requisition", err);
      await Swal.fire({
        title: "Delete failed",
        text: err?.response?.data?.message || "Unable to delete requisition. Please try again.",
        icon: "error",
      });
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
    setViewMessage(null);
  };

  const normalizeStockOption = (stock) => ({
    id: stock?.id ?? stock?.stock_id ?? stock?.value,
    name: stock?.name ?? stock?.stock_name ?? stock?.product_name ?? stock?.title ?? `Stock ${stock?.id ?? stock?.stock_id ?? ""}`,
  });

  const loadEditMeta = async () => {
    try {
      const [departmentRes, stockRes] = await Promise.all([
        api.get("/v1/departments", { params: withCompanyId({}) }),
        api.get("/v1/fetch_stocks", { params: withCompanyId({}) }),
      ]);

      const departmentsData = departmentRes.data?.data?.data || departmentRes.data?.data || departmentRes.data || [];
      const stockData = stockRes.data?.data?.data || stockRes.data?.data || stockRes.data || [];

      setEditDepartments(
        Array.isArray(departmentsData)
          ? departmentsData.map((department) => ({
              id: department.id ?? department.department_id ?? department.value,
              name: department.name ?? department.department_name ?? department.title ?? `Department ${department.id ?? department.department_id ?? ""}`,
            }))
          : []
      );

      setEditStockOptions(
        Array.isArray(stockData)
          ? stockData.map(normalizeStockOption)
          : []
      );
    } catch (err) {
      console.error("Failed to load edit requisition metadata", err);
      setEditDepartments([]);
      setEditStockOptions([]);
    }
  };

  const handleEdit = async (item) => {
    const requestId = item?.request_id || item?.order_id || item?.id;
    setShowEditModal(true);
    setEditLoading(true);
    setEditMessage(null);
    setEditForm({
      request_id: requestId || "",
      narration: "",
      department_id: "",
      items: [{ stock_id: "", stock_name: "", quantity: 1 }],
    });

    try {
      await loadEditMeta();
      const data = await fetchViewData(requestId);
      const entries = Array.isArray(data) ? data : data ? [data] : [];
      const firstEntry = entries[0] || {};

      const items = entries.length
        ? entries.map((entry) => ({
            stock_id: entry?.stock_id || entry?.item_id || entry?.stocks?.id || "",
            stock_name: entry?.stocks?.name || entry?.name || entry?.stock_name || "",
            quantity: Number(entry?.quantity ?? 1) || 1,
          }))
        : [{ stock_id: "", stock_name: "", quantity: 1 }];

      setEditForm({
        request_id: requestId || firstEntry.request_id || firstEntry.order_id || "",
        narration: extractNarrationFromDetail(data) || "",
        department_id: firstEntry?.department?.id || firstEntry?.department_id || "",
        items,
      });
    } catch (err) {
      console.error("Failed to load requisition for edit", err);
      setEditMessage("Unable to load requisition details for editing.");
    } finally {
      setEditLoading(false);
    }
  };

  const updateEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateEditItem = (index, field, value) => {
    setEditForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addEditItemRow = () => {
    setEditForm((prev) => ({
      ...prev,
      items: [...prev.items, { stock_id: "", stock_name: "", quantity: 1 }],
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editForm.request_id) {
      setEditMessage("This requisition could not be identified for update.");
      return;
    }
    if (!editForm.narration.trim()) {
      setEditMessage("Please enter a narration before updating the requisition.");
      return;
    }
    if (!editForm.department_id) {
      setEditMessage("Please select a department before updating the requisition.");
      return;
    }
    if (!editForm.items.length) {
      setEditMessage("Please add at least one item before updating the requisition.");
      return;
    }

    const stockName = [];
    const quantity = [];

    for (const [index, item] of editForm.items.entries()) {
      if (!item.stock_id) {
        setEditMessage(`Please select a stock item for row ${index + 1}.`);
        return;
      }

      const numericQty = Number(item.quantity);
      if (!Number.isFinite(numericQty) || numericQty < 0) {
        setEditMessage(`Please enter a valid quantity for row ${index + 1}.`);
        return;
      }

      stockName.push(String(item.stock_id));
      quantity.push(numericQty);
    }

    const totalQuantity = quantity.reduce((sum, value) => sum + value, 0);
    const payload = {
      request_id: editForm.request_id,
      narration: editForm.narration.trim(),
      total_quantity: totalQuantity,
      department_id: editForm.department_id,
      stock_name: stockName,
      quantity,
    };

    setEditSaving(true);
    setEditMessage(null);
    try {
      await api.post("/v1/stocks/update-request", withCompanyId(payload));
      setEditMessage("Requisition updated successfully.");
      await fetchInvoices();
      setShowEditModal(false);
    } catch (err) {
      console.error("Failed to update requisition", err);
      setEditMessage(err?.response?.data?.message || "Unable to update requisition. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div>
      <HeaderNav activePage={pageMap[location.pathname]} />

      <div className={classes.Container}>
        <div className={classes.actionsbtn}>
          <button className={classes.btns} onClick={() => navigate("/transactions/sales-invoice/sales")}>Sales Invoice</button>
          <button className={classes.btns} onClick={() => navigate("/inventory/stocks_delivery")}>Stock Delivary</button>
          <button
            className={`${classes.btns} ${location.pathname === "/inventory/purchaseOrder" ? classes.active : ""
              }`}
            onClick={() => navigate("/inventory/purchaseOrder")}
          >
            Stock Requisition
          </button>           {/* <button className={classes.btns}>All Invoices</button> */}
          <button className={`${classes.btns} ${classes.greens}`} onClick={() => navigate("/settings/product-services")}>Product & Services</button>
        </div>

        <header className={classes.Header}>
          <h2 className={classes.Title}>Stock Requisition</h2>
          <h1 className={classes.Titles}>
            Add, View, Edit and Delete Stock Requisition.
          </h1>
        </header>

        <div className={classes.entrjj}>
          <div className={classes.allkads}>
            <div className={classes.thrdkad}>
              <div className={classes.kad}>
                <p className={classes.kdptg}>Total Requisition</p>
                <p className={classes.kdptg1}>{cardMetrics.totalAmount > 0 ? formatMoney(cardMetrics.totalAmount) : cardMetrics.totalQuantity}</p>
                <div className={classes.rvnue}>
                  <p className={classes.skaltxt}>This month</p>
                </div>
              </div>
            </div>
            <div className={classes.frtkad}>
              <div className={classes.kad}>
                <p className={classes.kdptg}>Pending</p>
                <p className={classes.kdptg2}>{cardMetrics.pendingAmount > 0 ? formatMoney(cardMetrics.pendingAmount) : "0"}</p>
                <div className={classes.rvnue}>
                  <p className={classes.skaltxt}>Pending requisitions</p>
                </div>
              </div>
            </div>
            <div className={classes.fiftkad}>
              <div className={classes.kad}>
                <p className={classes.kdptg}>Released</p>
                <p className={classes.kdptg3}>{cardMetrics.releasedAmount > 0 ? formatMoney(cardMetrics.releasedAmount) : "0"}</p>
                <div className={classes.rvnue}>
                  <p className={classes.skaltxt}>Past due dates</p>
                </div>
              </div>
            </div>
            <div className={classes.frstkad}>
              <div className={classes.kad}>
                <p className={classes.kdptg}>This month</p>
                <p className={classes.kdptg4}>{cardMetrics.approvedCount}</p>
                <div className={classes.rvnue}>
                  <p className={classes.skaltxt}>Total Approved</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.headerActions}>
          <div className={classes.actionsRight}>
            <div className={classes.inputWrapper}>
              <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
              <input type="text" className={classes.searchBar} placeholder="Search sales" />
            </div>
            <div className={classes.selectWrapper}>
              <select className={classes.dropdown}>
                <option value="">All Status</option>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
                <option value="Overdue">Overdue</option>
                <option value="Draft">Draft</option>
              </select>
              <span className={classes.dropdownArrow}>▼</span>
            </div>

            <div className={classes.inputWrapper}>
              <input type="text" className={classes.searchBars} placeholder="Filter..." />
              <img src={filter} alt="Filter" className={classes.inputIcon} />
            </div>
          </div>
          <button className={classes.btnss} onClick={() => navigate("/inventory/manageRequisition")}>Manage Requisition</button>
        </div>

        <div className={classes.invoicecont}>
          <table className={classes.invoiceTable}>
              <thead style={{ whiteSpace: "nowrap" }}>
                <tr>
                  <th>S/N</th>
                  <th>Department Name</th>
                  <th>Narration</th>
                  {/* <th>Description</th> */}
                  <th>Requisition ID</th>
                  <th>Approval Status</th>
                  {/* <th>Payment Status</th> */}
                  <th>Quantity</th>
                  <th>Post Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array(5)
                    .fill()
                    .map((_, idx) => (
                      <tr key={idx}>
                        {Array(8)
                          .fill()
                          .map((_, i) => (
                            <td key={i}>
                              <Skeleton height={20} style={{ marginBottom: 10 }} />
                            </td>
                          ))}
                      </tr>
                    ))
                  : groupedRequisitions.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center" }}>
                        No stock requisitions found.
                      </td>
                    </tr>
                  ) : (
                    groupedRequisitions.map((item, index) => {
                      const narration = getNarration(item);
                      return (
                      <tr key={item.request_id || item.order_id || item.id}>
                        <td>{index + 1}</td>
                        <td>{item.department?.name || "—"}</td>
                        <td
                          className={classes.narrationCell}
                          title={narration !== "—" ? narration : undefined}
                        >
                          {truncateNarration(narration)}
                        </td>
                        <td>{item.order_id || item.request_id || "—"}</td>

                        {/* Approval Status */}
                        <td className={getApprovalStatusClass(item)}>
                          {getApprovalStatusLabel(item)}
                        </td>

                        {/* Quantity — sum of all line items in this requisition */}
                        <td style={{ textAlign: "right" }}>
                          {item.totalQuantity ?? 0}
                        </td>

                        {/* Post Date */}
                        <td>{getPostDate(item)}</td>

                        {/* Actions */}
                        <td className={classes.actions}>
                          <Pen
                            size={16}
                            className={classes.editIcon}
                            onClick={isRequisitionApproved(item) ? undefined : () => handleEdit(item)}
                            style={{
                              cursor: isRequisitionApproved(item) ? "not-allowed" : "pointer",
                              opacity: isRequisitionApproved(item) ? 0.4 : 1,
                            }}
                          />
                          <Trash2
                            size={16}
                            className={classes.deleteIcon}
                            style={{ cursor: "pointer", margin: "0 8px" }}
                            onClick={() => handleDelete(item)}
                          />
                          {/* <Printer
                            size={16}
                            className={classes.printIcon}
                            onClick={() => handlePrint(item)}
                            style={{ cursor: "pointer" }}
                          /> */}
                          <Eye
                            size={16}
                            style={{ margin: "0 8px", color: "black", cursor: "pointer" }}
                            onClick={() => handleView(item)}
                          />
                        </td>
                      </tr>
                      );
                    })
                  )}
              </tbody>
          </table>

          <div className={classes.paginationContainer}>
            <div className={classes.paginationControls}>
              <button>First</button>
              <button className={classes.active}>1</button>
              <button>Last</button>
              <span className={classes.pageText}>Displaying 1 - 41 of 41</span>
            </div>
          </div>

          {showEditModal && (
            <div className={classes.modalOverlay} onClick={() => setShowEditModal(false)}>
              <div className={classes.editModalContent} onClick={(e) => e.stopPropagation()}>
                <div className={classes.modalHeader}>
                  <h2>Edit Requisition</h2>
                  <button className={classes.modalClose} onClick={() => setShowEditModal(false)}>
                    ×
                  </button>
                </div>
                <div className={classes.modalBody}>
                  {editLoading ? (
                    <p>Loading requisition details…</p>
                  ) : (
                    <form onSubmit={handleEditSubmit} className={classes.editForm}>
                      <div className={classes.editFormGroup}>
                        <label>Department</label>
                        <select
                          className={classes.editSelect}
                          value={editForm.department_id}
                          onChange={(e) => updateEditField("department_id", e.target.value)}
                        >
                          <option value="">Select department</option>
                          {editDepartments.map((department) => (
                            <option key={department.id} value={department.id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={classes.editFormGroup}>
                        <label>Narration</label>
                        <textarea
                          className={classes.editTextarea}
                          value={editForm.narration}
                          onChange={(e) => updateEditField("narration", e.target.value)}
                          rows={4}
                        />
                      </div>

                      <div className={classes.editItemsSection}>
                        <div className={classes.editItemsHeader}>
                          <h3>Items</h3>
                          <button type="button" className={classes.addItemButton} onClick={addEditItemRow}>
                            + Add Item
                          </button>
                        </div>
                        {editForm.items.map((item, index) => (
                          <div key={`${item.stock_id || index}-${index}`} className={classes.editItemRow}>
                            <div className={classes.editFormGroup}>
                              <label>Stock Item</label>
                              <select
                                className={classes.editSelect}
                                value={item.stock_id}
                                onChange={(e) => {
                                  const selectedStock = editStockOptions.find((stock) => String(stock.id) === String(e.target.value));
                                  updateEditItem(index, "stock_id", e.target.value);
                                  updateEditItem(index, "stock_name", selectedStock?.name || "");
                                }}
                              >
                                <option value="">Select stock</option>
                                {editStockOptions.map((stock) => (
                                  <option key={stock.id} value={stock.id}>
                                    {stock.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className={classes.editFormGroup}>
                              <label>Quantity</label>
                              <input
                                type="number"
                                min="0"
                                className={classes.editInput}
                                value={item.quantity}
                                onChange={(e) => updateEditItem(index, "quantity", e.target.value)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {editMessage && <div className={classes.modalMessage}>{editMessage}</div>}

                      <div className={classes.modalActions}>
                        <button type="button" className={classes.modalActionButtonSecondary} onClick={() => setShowEditModal(false)}>
                          Cancel
                        </button>
                        <button type="submit" className={classes.modalActionButton} disabled={editSaving}>
                          {editSaving ? "Updating..." : "Update Requisition"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}

          {showViewModal && (
            <div className={classes.modalOverlay} onClick={closeViewModal}>
              <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={classes.modalHeader}>
                  <h2>Requisition Details</h2>
                  <button className={classes.modalClose} onClick={closeViewModal}>
                    ×
                  </button>
                </div>
                <div className={classes.modalBody}>
                  {viewLoading ? (
                    <p>Loading details…</p>
                  ) : Array.isArray(viewData) && viewData.length > 0 ? (
                    viewData.map((entry, idx) => (
                      <div key={entry.id || idx} className={classes.requisitionSection}>
                        <div className={classes.sectionHeader}>
                          <h3>Item {idx + 1}</h3>
                          <span>{entry.request_id || entry.order_id || `ID ${entry.id || idx}`}</span>
                        </div>
                        <div className={classes.detailGrid}>
                          <div className={classes.detailRow}>
                            <span>Department</span>
                            <strong>{entry.department?.name || "—"}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Request ID</span>
                            <strong>{entry.request_id || "—"}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Order ID</span>
                            <strong>{entry.order_id || "—"}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Stock Name</span>
                            <strong>{entry.stocks?.name || entry.name || entry.stock_name || "—"}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Quantity</span>
                            <strong>{entry.quantity ?? "—"}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Narration</span>
                            <strong>{entry.narration || entry.description || "—"}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Approval Status</span>
                            <strong>{getApprovalStatusLabel(entry)}</strong>
                          </div>
                          <div className={classes.detailRow}>
                            <span>Created At</span>
                            <strong>{entry.created_at ? entry.created_at.split("T")[0] : "—"}</strong>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : viewData ? (
                    <div className={classes.requisitionSection}>
                      <div className={classes.sectionHeader}>
                        <h3>Item 1</h3>
                        <span>{viewData.request_id || viewData.order_id || `ID ${viewData.id || "—"}`}</span>
                      </div>
                      <div className={classes.detailGrid}>
                        <div className={classes.detailRow}>
                          <span>Department</span>
                          <strong>{viewData.department?.name || "—"}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Request ID</span>
                          <strong>{viewData.request_id || "—"}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Order ID</span>
                          <strong>{viewData.order_id || "—"}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Stock Name</span>
                          <strong>{viewData.stocks?.name || viewData.name || viewData.stock_name || "—"}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Quantity</span>
                          <strong>{viewData.quantity ?? "—"}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Narration</span>
                          <strong>{viewData.narration || viewData.description || "—"}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Approval Status</span>
                          <strong>{getApprovalStatusLabel(viewData)}</strong>
                        </div>
                        <div className={classes.detailRow}>
                          <span>Created At</span>
                          <strong>{viewData.created_at ? viewData.created_at.split("T")[0] : "—"}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p>No requisition details available.</p>
                  )}
                </div>
                {viewMessage && (
                  <div className={classes.modalMessage}>
                    {viewMessage}
                  </div>
                )}
                {getViewActionInfo(viewData) && (
                  <div className={classes.modalActions}>
                    <button
                      type="button"
                      className={classes.modalActionButton}
                      disabled={viewActionLoading}
                      onClick={handleViewAction}
                    >
                      {viewActionLoading ? `${getViewActionInfo(viewData).label}ing...` : getViewActionInfo(viewData).label}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default StockRequisition;
